import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { IMG } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";
import { PageBanner } from "@/components/ui/PageBanner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle, CreditCard } from "lucide-react";

const PLAN_NAMES = { individual_monthly: "Individual", club_monthly: "Club", starter_monthly: "Initiate (old)", pro_monthly: "Operator (old)", elite_monthly: "Fleet Command (old)" };
const statusIcon = { paid: CheckCircle2, pending: Clock, failed: XCircle, expired: XCircle, initiated: Clock };
const statusColor = { paid: "#CCFF00", pending: "#FFB340", failed: "#FF3B30", expired: "#565C6F", initiated: "#FFB340" };

export default function Payments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: history = [] } = useQuery({ queryKey: ["payments-history"], queryFn: async () => (await api.get("/api/payments/history")).data });

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.membership} overline="billing" title="Payments"
        subtitle="Your membership, receipts and payment history in one place."
        actions={<Button onClick={() => navigate("/app/membership")} className="bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="go-memberships-btn">Manage membership</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="surface rounded-lg p-6 md:col-span-1">
          <CreditCard className="w-5 h-5 text-acid mb-3" />
          <p className="mono-label">Current membership</p>
          <p className="font-heading font-black text-2xl mt-1">{user?.membership_status === "trialing" ? "Free taster" : (PLAN_NAMES[user?.membership_plan] || "None")}</p>
          <span className="inline-block mt-2 mono-label px-2.5 py-1 rounded-full border"
            style={{ color: user?.membership_status === "active" ? "#CCFF00" : "#565C6F", borderColor: "#1E222A" }}>
            {user?.membership_status || "inactive"}
          </span>
        </div>
        <div className="surface rounded-lg p-6 md:col-span-2">
          <p className="mono-label mb-3">Transaction history</p>
          <div className="space-y-2">
            {history.map((t, i) => {
              const Icon = statusIcon[t.payment_status] || Clock;
              return (
                <motion.div key={t.session_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                  className="flex items-center justify-between border-b border-[#1E222A] py-2.5" data-testid={`txn-${i}`}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4" style={{ color: statusColor[t.payment_status] || "#565C6F" }} />
                    <div>
                      <p className="text-sm">{PLAN_NAMES[t.lookup_key] || t.lookup_key}</p>
                      <p className="mono-label">{t.created_at?.slice(0, 10)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono2 text-sm">${t.amount} {t.currency?.toUpperCase()}</p>
                    <p className="mono-label" style={{ color: statusColor[t.payment_status] || "#565C6F" }}>{t.payment_status}</p>
                  </div>
                </motion.div>
              );
            })}
            {history.length === 0 && (
              <div className="text-center py-10">
                <p className="text-[#8A91A6] text-sm">No transactions yet.</p>
                <Button onClick={() => navigate("/app/membership")} className="mt-4 bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="empty-membership-btn">Choose a plan</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
